import {
  ShowToast as CairoShowToast,
  ToastAction as CairoToastAction,
  ToastConfig as CairoToastConfig,
} from '../model/toast';

type CairoToastConfigRequired = Required<CairoToastConfig>;
type ToastActionRequired = Required<CairoToastAction>;

export const toastConfigMaps: {
  priority: Record<
    CairoToastConfigRequired['priority'],
    ToastConfig['priority']
  >;
  timeout: Record<CairoToastConfigRequired['timeout'], ToastConfig['timeout']>;
  type: Record<CairoToastConfigRequired['type'], ToastConfig['type']>;
  uiType: Record<ToastActionRequired['uiType'], ToastAction['uiType']>;
} = {
  priority: {
    LOW: 'low',
    HIGH: 'high',
    NORMAL: 'normal',
  },
  timeout: {
    NORMAL: 'normal',
    NONE: 'none',
  },
  type: {
    STANDARD: 'standard',
    ERROR: 'error',
    PREMIUM: 'premium' as 'standard',
    SUCCESS: 'success',
    WARNING: 'warning',
  },
  uiType: {
    BUTTON: 'button',
    LINK: 'link',
  },
};

export function showToastCompat(showToast: ShowToast): CairoShowToast {
  return ({
    message,
    type,
    timeout,
    priority,
    action,
    onCloseClick,
    onToastSeen,
    ...rest
  }) => {
    const showToastResult = showToast({
      message,
      priority: priority ? toastConfigMaps.priority[priority] : undefined,
      timeout: timeout ? toastConfigMaps.timeout[timeout] : undefined,
      type: type ? toastConfigMaps.type[type] : undefined,
      action:
        action &&
        ({
          text: action.text,
          onClick: action.onClick,
          removeToastOnClick: action.removeToastOnClick,
          uiType: action.uiType
            ? toastConfigMaps.uiType[action.uiType]
            : undefined,
        } as ToastConfig['action']),
      onCloseClick,
      onToastSeen,
      ...rest,
    });

    return {
      remove: () => {
        (async () => {
          (await showToastResult).remove();
        })();
      },
    };
  };
}

type ToastTimeout = 'normal' | 'none';
type ToastType = 'standard' | 'success' | 'warning' | 'error';
type ToastPriority = 'low' | 'normal' | 'high';
type ToastActionUiType = 'button' | 'link';
type ToastAction = {
  text: string;
  uiType?: ToastActionUiType;
  removeToastOnClick?: boolean;
  onClick: () => void;
};
type ToastConfig = {
  message: string;
  timeout?: ToastTimeout;

  type?: ToastType;

  priority?: ToastPriority;
  action?: ToastAction;
  onCloseClick?: () => void;
  onToastSeen?: () => void;
};
type ShowToast = (config: ToastConfig) =>
  | {
      remove: () => void;
    }
  | Promise<{
      remove: () => void;
    }>;
